/**
 * @fileoverview Job Completion Management Hook
 * 
 * Manages the job completion workflow including submission, review, acceptance/rejection,
 * and admin reopening capabilities. Integrates with proof of delivery, financial reconciliation,
 * and payment processing systems.
 * 
 * Completion Workflow:
 * 1. Vendor checks completion requirements (milestones, delivery proof, no pending modifications)
 * 2. Vendor submits completion with notes and delivery date
 * 3. System creates job_completions record and updates job status to 'completed_pending_review'
 * 4. Client has 15 business days to review and accept/reject (auto-accepts after deadline)
 * 5. On acceptance:
 *    a. Financial reconciliation is created and processed
 *    b. Job status updated to 'accepted'
 *    c. Payment transfer queue entry created for vendor payout
 * 6. On rejection: Job returns to 'in_progress' for rework
 * 
 * Financial Integration:
 * - Creates reconciliation before status update to prevent race conditions
 * - Processes adjustments (refunds/charges) based on PO modifications
 * - Triggers payment transfer queue for vendor payout
 * - Handles ACH vs card payment settlement timelines
 * 
 * Key Features:
 * - Comprehensive completion requirements validation
 * - 15 business day auto-acceptance with countdown timer
 * - Financial reconciliation integration
 * - Admin override capabilities for reopening jobs
 * - Error tracking and retry logic
 * - Non-blocking notification system
 * 
 * @module useJobCompletion
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { JobCompletion, Document, DocumentUpload } from '@/lib/types/completion';
import { useToast } from '@/hooks/use-toast';
import { errorTracking } from '@/lib/errorTracking';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

/**
 * Completion requirements that must be met before a vendor can submit completion.
 * 
 * These checks ensure the job is in a valid state for completion:
 * - All milestones must be marked complete
 * - Proof of delivery must be uploaded and accepted
 * - No pending PO modifications (ensures financial clarity)
 * - Job must be in 'in_progress' status
 */
export interface CompletionRequirements {
  milestones_complete: boolean;
  total_milestones: number;
  completed_milestones: number;
  proof_of_delivery_uploaded: boolean;
  no_pending_modifications: boolean;
  job_status_valid: boolean;
  can_submit: boolean;
}

/**
 * Completion data with archive and acceptance tracking fields.
 * 
 * Tracks the lifecycle of a completion submission from initial submission
 * through client review to final acceptance/rejection.
 */
export interface JobCompletionData {
  id: string;
  job_id: string;
  completion_submitted_at?: string;
  completion_reviewed_at?: string;
  accepted_at?: string;
  auto_accepted?: boolean;
  archived_at?: string;
  is_archived?: boolean;
  status: string;
}

/**
 * Hook for managing job completion workflow
 * 
 * Provides comprehensive job completion management including:
 * - Fetching completion requirements and current completion data
 * - Submitting job completion with delivery details
 * - Reviewing completion (accept/reject) with financial reconciliation
 * - Admin capabilities to reopen completed/rejected jobs
 * - Auto-acceptance countdown timer (30 business days)
 * - Error tracking and retry logic for reliability
 * 
 * The hook uses database RPC functions for atomic operations and integrates
 * with the financial reconciliation system to ensure accurate payment processing.
 * 
 * @param jobId - The public ID of the job (not the UUID)
 * @returns Object with completion data, requirements, submission/review functions, and utilities
 * 
 * @example
 * ```tsx
 * function JobCompletionPage({ jobPublicId }) {
 *   const {
 *     requirements,
 *     completionData,
 *     loading,
 *     submitCompletion,
 *     reviewCompletion,
 *     getTimeRemaining
 *   } = useJobCompletion(jobPublicId);
 * 
 *   // Vendor submission
 *   const handleSubmit = async () => {
 *     if (!requirements?.can_submit) {
 *       alert('Requirements not met');
 *       return;
 *     }
 *     await submitCompletion('Work completed per specifications', new Date());
 *   };
 * 
 *   // Client review
 *   const handleAccept = async () => {
 *     await reviewCompletion('accepted', 'Work meets expectations');
 *     // Triggers financial reconciliation and payment processing
 *   };
 * 
 *   // Show countdown
 *   const timeLeft = completionData?.completion_submitted_at 
 *     ? getTimeRemaining(completionData.completion_submitted_at)
 *     : null;
 * 
 *   return (
 *     <div>
 *       {timeLeft && (
 *         <p>Auto-accepts in {timeLeft.days}d {timeLeft.hours}h</p>
 *       )}
 *       <button onClick={handleSubmit}>Submit Completion</button>
 *       <button onClick={handleAccept}>Accept Work</button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useJobCompletion = (jobId: string) => {
  const [completion, setCompletion] = useState<JobCompletion | null>(null);
  const [requirements, setRequirements] = useState<CompletionRequirements | null>(null);
  const [completionData, setCompletionData] = useState<JobCompletionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchRequirements = useCallback(async () => {
    if (!jobId) return;
    
    try {
      setLoading(true);
      setError(null);
      let dataRes: any, errRes: any;
      for (let attempt = 0; attempt < 2; attempt++) {
        const { data, error } = await supabase.rpc('check_job_completion_requirements', {
          job_public_id: jobId
        });
        dataRes = data; errRes = error;
        if (!error) break;
        await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
      }

      if (errRes) throw errRes;
      setRequirements(dataRes as unknown as CompletionRequirements);
    } catch (error) {
      console.error('Error fetching completion requirements:', error);
      setError('Failed to fetch completion requirements');
      // Only show toast for non-network errors to avoid spam
      if (!error?.message?.includes('Failed to fetch')) {
        toast({
          title: "Error",
          description: "Failed to fetch completion requirements",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, toast]);

  const fetchCompletionData = useCallback(async () => {
    if (!jobId) return;
    
    try {
      let dataRes: any, errRes: any;
      for (let attempt = 0; attempt < 2; attempt++) {
        const { data, error } = await supabase
          .from('jobs')
          .select(`
            id,
            job_id: id,
            completion_submitted_at,
            completion_reviewed_at,
            accepted_at,
            auto_accepted,
            archived_at,
            is_archived,
            status
          `)
          .eq('public_id', jobId)
          .maybeSingle();
        dataRes = data; errRes = error;
        if (!error) break;
        await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
      }

      if (errRes) throw errRes;
      setCompletionData(dataRes);
    } catch (error) {
      console.error('Error fetching completion data:', error);
    }
  }, [jobId]);

  const fetchCompletion = useCallback(async () => {
    try {
      setLoading(true);

      // Resolve the internal job UUID from the public_id
      let jobRow: any = null; let jobErr: any = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        const resp = await supabase
          .from('jobs')
          .select('id')
          .eq('public_id', jobId)
          .maybeSingle();
        jobRow = resp.data; jobErr = resp.error;
        if (!jobErr) break;
        await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
      }

      if (jobErr) throw jobErr;
      if (!jobRow?.id) {
        setCompletion(null);
        return;
      }

      let completionDataRes: any = null; let completionErr: any = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        const resp = await supabase
          .from('job_completions')
          .select('*')
          .eq('job_id', jobRow.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        completionDataRes = resp.data; completionErr = resp.error;
        if (!completionErr) break;
        await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
      }

      if (completionErr) throw completionErr;

      setCompletion(completionDataRes as JobCompletion | null);
      setError(null);
    } catch (err) {
      console.error('Error fetching job completion:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch completion');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      fetchRequirements();
      fetchCompletionData();
      fetchCompletion();
    }
  }, [jobId]);

  /**
   * Submits job completion for client review
   * 
   * Creates a completion record and updates job status to 'completed_pending_review'.
   * The client then has 15 business days to review before auto-acceptance occurs.
   * 
   * Process:
   * 1. Validates completion requirements are met
   * 2. Calls RPC function to create completion record
   * 3. Sends notification to client (non-blocking)
   * 4. Refreshes completion data (non-blocking)
   * 
   * @param completionNotes - Optional notes about the completion
   * @param deliveryDate - Actual delivery date
   * @returns True if submission successful, false otherwise
   */
  const submitCompletion = async (completionNotes?: string, deliveryDate?: Date) => {
    if (!jobId) return false;

    // Guard: ensure requirements allow submission to avoid confusing RPC errors
    if (requirements && !requirements.can_submit) {
      toast({
        title: 'Cannot Submit',
        description: 'Completion requirements are not met yet.',
        variant: 'destructive',
      });
      return false;
    }
    
    setSubmitting(true);
    
    try {
      // Validate and format deliveryDate
      let formattedDeliveryDate: string | null = null;
      if (deliveryDate) {
        if (!(deliveryDate instanceof Date) || isNaN(deliveryDate.getTime())) {
          throw new Error('Invalid delivery date provided');
        }
        formattedDeliveryDate = deliveryDate.toISOString();
      }

      const params = {
        job_public_id: jobId,
        completion_notes: completionNotes ?? null,
        delivery_date: formattedDeliveryDate,
      };

      // CRITICAL: Submit completion (must succeed)
      const { data, error } = await supabase.rpc('submit_job_completion', params);

      if (error) throw error;

      if ((data as any)?.error) {
        toast({
          title: 'Cannot Submit',
          description: (data as any).error,
          variant: 'destructive',
        });
        return false;
      }

      // Success! Show confirmation immediately
      toast({
        title: "Success",
        description: "Job completion submitted for client review",
      });

      // Track job marked complete (vendor submitting completion)
      analytics.track({
        name: ANALYTICS_EVENTS.JOB_MARKED_COMPLETE,
        category: 'engagement',
        properties: {
          job_id: jobId,
        },
      });

      // NON-CRITICAL: Try to send notifications (don't block on failure)
      try {
        const { data: jobData } = await supabase
          .from('jobs')
          .select('id, public_id, title, organization_id')
          .eq('public_id', jobId)
          .single();

        if (jobData) {
          await supabase.functions.invoke('job-completion-notify', {
            body: { type: 'submit', jobId: jobData.id },
          });
        }
      } catch (notifyErr) {
        console.error('Notification failed (non-critical):', notifyErr);
      }

      // NON-CRITICAL: Refresh data (don't block on failure)
      try {
        await Promise.all([fetchRequirements(), fetchCompletionData(), fetchCompletion()]);
      } catch (refreshErr) {
        console.error('Data refresh failed (non-critical):', refreshErr);
      }

      return true;
    } catch (error: any) {
      console.error('Error submitting completion:', error);
      const formatSupabaseError = (e: any) => e?.message || e?.details || e?.hint || e?.code || 'Failed to submit completion';

      // Non-blocking error report for diagnostics (don't await to prevent blocking)
      errorTracking.captureCustomError('submit_job_completion_failed', 'high', {
        jobId,
        params: {
          completionNotes: completionNotes ?? null,
          deliveryDate: deliveryDate?.toISOString() ?? null,
        },
        supabaseError: error && typeof error === 'object' ? JSON.parse(JSON.stringify(error)) : { message: String(error) },
      }).catch((reportErr) => {
        console.error('[submitCompletion] Error reporting failure (non-critical):', reportErr);
      });

      toast({
        title: 'Error',
        description: formatSupabaseError(error),
        variant: 'destructive',
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Reviews a job completion (accept or reject)
   * 
   * For accepted completions, CRITICAL ORDER OF OPERATIONS:
   * 1. Create financial reconciliation FIRST (prevents race conditions)
   * 2. Process reconciliation (refunds/charges)
   * 3. Update job status (triggers payment transfer queue)
   * 
   * This order ensures the payment transfer queue can find the reconciliation
   * record when calculating the final vendor payout amount.
   * 
   * For rejected completions:
   * - Returns job to 'in_progress' status
   * - Notifies vendor of rejection reason
   * - Allows vendor to resubmit after addressing issues
   * 
   * @param reviewResult - 'accepted' or 'rejected'
   * @param reviewNotes - Optional notes explaining the decision
   * @param reviewReasonCode - Optional structured reason code for rejections
   * @returns True if review successful, false otherwise
   */
  const reviewCompletion = async (
    reviewResult: 'accepted' | 'rejected',
    reviewNotes?: string,
    reviewReasonCode?: string
  ) => {
    if (!jobId) return false;
    
    setSubmitting(true);
    
    try {
      // CRITICAL: For accepted jobs, create reconciliation BEFORE calling RPC
      // This prevents race conditions where the trigger enqueues transfer before reconciliation exists
      if (reviewResult === 'accepted') {
        const { data: jobData } = await supabase
          .from('jobs')
          .select('id')
          .eq('public_id', jobId)
          .single();

        if (!jobData) throw new Error('Job not found');

        // Get purchase order ID
        const { data: poData } = await supabase
          .from('purchase_orders')
          .select('id')
          .eq('job_id', jobData.id)
          .single();

        if (!poData) throw new Error('Purchase order not found');

        // STEP 1: Create reconciliation record FIRST
        const { data: reconResult, error: reconError } = await supabase.functions.invoke(
          'create-financial-reconciliation',
          {
            body: { 
              jobId: jobData.id,
              purchaseOrderId: poData.id
            }
          }
        );

        if (reconError || !reconResult?.reconciliationId) {
          console.error('Failed to create reconciliation:', reconError);
          throw new Error('Failed to create reconciliation');
        }

        console.log('Reconciliation created:', reconResult.reconciliationId);

        // STEP 2: Process reconciliation (refunds/charges)
        const { data: processResult } = await supabase.functions.invoke('process-job-reconciliation', {
          body: { 
            jobId: jobData.id,
            purchaseOrderId: poData.id
          }
        });

        console.log('Reconciliation processed:', processResult);
      }

      // STEP 3: NOW call the RPC to update job status (triggers transfer queue)
      const { data, error } = await supabase.rpc('review_job_completion', {
        job_public_id: jobId,
        review_result: reviewResult,
        review_notes: reviewNotes,
        review_reason_code: reviewReasonCode
      });

      if (error) throw error;
      
      if ((data as any)?.error) {
        toast({
          title: "Cannot Review",
          description: (data as any).error,
          variant: "destructive",
        });
        return false;
      }

      // NON-CRITICAL: Show success toast
      try {
        const { data: jobData } = await supabase
          .from('jobs')
          .select('id, public_id, title, organization_id')
          .eq('public_id', jobId)
          .single();

        if (jobData) {
          // Send notification
          try {
            await supabase.functions.invoke('job-completion-notify', {
              body: { 
                type: 'review', 
                jobId: jobData.id, 
                reviewResult,
                reviewNotes,
                reviewReasonCode,
              },
            });
          } catch (notifyErr) {
            console.error('Notification failed (non-critical):', notifyErr);
          }

          // Show appropriate success message
          if (reviewResult === 'accepted') {
            toast({
              title: "Success",
              description: "Job completion accepted. Payment to vendor will be processed shortly.",
            });
          } else {
            toast({
              title: "Success",
              description: `Job completion ${reviewResult}`,
            });
          }
        } else {
          toast({
            title: "Success",
            description: `Job completion ${reviewResult}`,
          });
        }
      } catch (postReviewErr) {
        console.error('Post-review operations failed (non-critical):', postReviewErr);
        toast({
          title: "Success",
          description: `Job completion ${reviewResult}`,
        });
      }
      
      // NON-CRITICAL: Refresh data (don't block on failure)
      try {
        await Promise.all([fetchRequirements(), fetchCompletionData(), fetchCompletion()]);
      } catch (refreshErr) {
        console.error('Data refresh failed (non-critical):', refreshErr);
      }

      return true;
    } catch (error) {
      console.error('Error reviewing completion:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to review completion",
        variant: "destructive",
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Allows admins to reopen completed/rejected jobs
   * 
   * Admin override function to return a job to 'in_progress' status.
   * Useful for dispute resolution or when additional work is needed.
   * 
   * @param reopenReason - Explanation for reopening the job
   * @param newStatus - Target status (defaults to 'in_progress')
   * @returns True if reopen successful, false otherwise
   */
  const adminReopenJob = async (reopenReason: string, newStatus: string = 'in_progress') => {
    if (!jobId) return false;
    
    try {
      setSubmitting(true);
      const { data, error } = await supabase.rpc('admin_reopen_job', {
        job_public_id: jobId,
        reopen_reason: reopenReason,
        new_status: newStatus
      });

      if (error) throw error;
      
      if ((data as any)?.error) {
        toast({
          title: "Cannot Reopen",
          description: (data as any).error,
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Success",
        description: "Job reopened successfully",
      });
      
      // Refresh data
      await Promise.all([fetchRequirements(), fetchCompletionData(), fetchCompletion()]);
      return true;
    } catch (error) {
      console.error('Error reopening job:', error);
      toast({
        title: "Error",
        description: "Failed to reopen job",
        variant: "destructive",
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Calculates time remaining until auto-acceptance (30 business days)
   * 
   * Uses business day calculation that excludes weekends and US federal holidays.
   * The actual auto_accept_at date is stored in job_completions and set by the database.
   * This function calculates remaining time based on the auto_accept_at from the completion record.
   * 
   * @param submittedAt - ISO timestamp of completion submission (used as fallback)
   * @returns Object with days/hours remaining and deadline, or null if expired
   */
  const getTimeRemaining = (submittedAt: string) => {
    // If we have a completion with auto_accept_at, use that directly
    if (completion?.auto_accept_at) {
      const deadline = new Date(completion.auto_accept_at);
      const now = new Date();
      const timeLeft = deadline.getTime() - now.getTime();
      
      if (timeLeft <= 0) return null;
      
      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      return { days, hours, deadline };
    }
    
    // Fallback: estimate ~21 calendar days for 15 business days
    const submitted = new Date(submittedAt);
    const deadline = new Date(submitted.getTime() + 21 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const timeLeft = deadline.getTime() - now.getTime();
    
    if (timeLeft <= 0) return null;
    
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return { days, hours, deadline };
  };

  const refetch = useCallback(() => {
    return Promise.all([fetchRequirements(), fetchCompletionData(), fetchCompletion()]);
  }, [fetchRequirements, fetchCompletionData, fetchCompletion]);

  return {
    requirements,
    completionData,
    completion,
    loading,
    submitting,
    error,
    submitCompletion,
    reviewCompletion,
    adminReopenJob,
    getTimeRemaining,
    refetch
  };
};